class SwiperCarousel extends HTMLElement {
  constructor() {
    super();
    this.swiper = null;
    this.mediaQuery = null;
    this.handleMediaChange = this.handleMediaChange.bind(this);
  }

  connectedCallback() {
    const activeMaxWidth = this.dataset.activeMaxWidth;
    const activeMinWidth = this.dataset.activeMinWidth;

    if (activeMaxWidth || activeMinWidth) {
      const conditions = [];
      if (activeMinWidth) conditions.push(`(min-width: ${activeMinWidth}px)`);
      if (activeMaxWidth) conditions.push(`(max-width: ${activeMaxWidth}px)`);

      this.mediaQuery = window.matchMedia(conditions.join(' and '));
      this.mediaQuery.addEventListener('change', this.handleMediaChange);

      if (this.mediaQuery.matches) {
        this.setup();
      }
    } else {
      this.setup();
    }
  }

  disconnectedCallback() {
    if (this.mediaQuery) {
      this.mediaQuery.removeEventListener('change', this.handleMediaChange);
    }
    this.teardown();
  }

  handleMediaChange(event) {
    if (event.matches) {
      this.setup();
    } else {
      this.teardown();
    }
  }

  setup() {
    if (this.swiper) return;

    this.whenSwiperReady(() => {
      // The breakpoint may have flipped back, or the element may have been
      // removed, while we were waiting for the library to finish loading.
      if (this.swiper || !this.isConnected) return;

      const el = this.querySelector('.swiper');
      if (!el) return;

      this.swiper = new window.Swiper(el, this.buildOptions());
    });
  }

  teardown() {
    if (this.swiper) {
      this.swiper.destroy(true, true);
      this.swiper = null;
    }
  }

  // swiper-bundle.min.js loads with `defer`, so it can still be mid-download
  // when this custom element upgrades and connects. Poll for the global
  // instead of assuming it's ready — the CSS fallback in swiper-carousel.css
  // keeps slides usable (native scroll-snap) for the moment in between.
  whenSwiperReady(callback) {
    if (window.Swiper) {
      callback();
      return;
    }

    const start = Date.now();
    const timeout = 10000;

    const poll = () => {
      if (window.Swiper) {
        callback();
        return;
      }
      if (Date.now() - start > timeout) return;
      requestAnimationFrame(poll);
    };

    poll();
  }

  buildOptions() {
    const data = this.dataset;
    const options = {
      slidesPerView: data.slidesPerView ? parseFloat(data.slidesPerView) : 1,
      spaceBetween: data.spaceBetween ? parseFloat(data.spaceBetween) : 16,
      loop: data.loop === 'true',
      watchOverflow: true,
    };

    if (data.autoplayDelay) {
      options.autoplay = {
        delay: parseInt(data.autoplayDelay, 10),
        disableOnInteraction: false,
      };
    }

    if (data.breakpoints) {
      try {
        options.breakpoints = JSON.parse(data.breakpoints);
      } catch (error) {
        console.warn('swiper-carousel: invalid data-breakpoints JSON', error);
      }
    }

    const pagination = this.querySelector('.swiper-pagination');
    if (pagination) {
      options.pagination = {
        el: pagination,
        clickable: true,
      };
    }

    // data-nav-next/data-nav-prev let a section point Swiper at nav buttons
    // that live outside this element (e.g. shared arrows rendered once in a
    // section header instead of duplicated per carousel instance).
    const nextEl = (data.navNext && document.getElementById(data.navNext)) || this.querySelector('.swiper-button-next');
    const prevEl = (data.navPrev && document.getElementById(data.navPrev)) || this.querySelector('.swiper-button-prev');
    if (nextEl || prevEl) {
      options.navigation = { nextEl, prevEl };
    }

    return options;
  }
}

customElements.define('swiper-carousel', SwiperCarousel);
